import React from 'react';
import { Play, RotateCcw, Trash2, Loader2, Mic, FileQuestion } from 'lucide-react';
import { AiInterview } from '../../types';
import { INTERVIEW_DIFFICULTY_META, INTERVIEW_TYPE_META } from './interviewConstants';

interface Props {
  interview: AiInterview;
  busy: boolean;
  onStart: () => void;
  onDelete: () => void;
}

const InterviewReadyCard: React.FC<Props> = ({ interview, busy, onStart, onDelete }) => {
  const isResume = interview.status === 'IN_PROGRESS';
  const answered = interview.transcript.filter((l) => l.speaker === 'CANDIDATE').length;

  return (
    <div className="max-w-lg mx-auto">
      <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-btn bg-brand-50 flex items-center justify-center shrink-0">
            <Mic className="w-5 h-5 text-brand-500" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-foreground text-lg leading-tight">
              {isResume ? 'Resume your interview' : 'Interview ready'}
            </h3>
            <p className="text-sm text-zinc-500 mt-0.5">
              {isResume
                ? 'You have an interview in progress. Pick up where you left off.'
                : 'Your AI interviewer is ready to go.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          <span className={`text-[11px] font-semibold px-2 py-1 rounded ${INTERVIEW_TYPE_META[interview.interviewType].badge}`}>
            {INTERVIEW_TYPE_META[interview.interviewType].label}
          </span>
          <span className={`text-[11px] font-semibold px-2 py-1 rounded ${INTERVIEW_DIFFICULTY_META[interview.difficulty ?? 'MID_LEVEL'].badge}`}>
            {INTERVIEW_DIFFICULTY_META[interview.difficulty ?? 'MID_LEVEL'].label}
          </span>
          <span className="text-[11px] font-medium text-zinc-500 bg-zinc-100 px-2 py-1 rounded">
            {interview.maxQuestions} questions
          </span>
          <span className="text-[11px] font-medium text-zinc-500 bg-zinc-100 px-2 py-1 rounded inline-flex items-center gap-1">
            <FileQuestion className="w-3 h-3" /> {answered} answered
          </span>
        </div>

        <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-100 rounded-card px-4 py-3 mb-6">
          {isResume ? (
            'The interviewer will pick up from your last answer and continue. Microphone access is required.'
          ) : (
            'Speak out loud when the interviewer asks a question — you can interrupt at any time. A full transcript and evaluation are saved to this application.'
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onStart}
            disabled={busy}
            className="h-11 px-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-btn transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : isResume ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {busy ? 'Connecting…' : isResume ? 'Resume interview' : 'Start interview'}
          </button>
          <button
            onClick={onDelete}
            className="h-11 px-4 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-btn transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Discard
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewReadyCard;